import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Search } from "semantic-ui-react";
import _ from "lodash";
import Constants from "../util/Constants";
import Fuse from "fuse.js";
import { getMenuItems } from "../util/MenuItems";

const SymbolSearch = ({
  onSymbolSelect,
  onMenuSelect,
  userEmail = "",
  menuItems = [],
  dynamicItems = {},
  placeholder = "Search Company Name, Ticker, or Menu...",
  initialValue = "",
  fluid = false,
  style = {},
  className = "",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [symbolResults, setSymbolResults] = useState([]);
  const [symbolsList, setSymbolsList] = useState([]);
  const [menuItemsList, setMenuItemsList] = useState([]);
  const [value, setValue] = useState(initialValue);

  // Memoize the symbols list to prevent unnecessary re-renders
  const memoizedSymbolsList = useMemo(() => symbolsList, [symbolsList]);

  // Initialize Fuse.js instances for stocks and menu items
  const stockFuse = useMemo(
    () =>
      new Fuse(memoizedSymbolsList, {
        keys: ["title", "description"],
        threshold: 0.3, // adjust for fuzziness
        minMatchCharLength: 1,
      }),
    [memoizedSymbolsList]
  );

  // Extract arrays from dynamicItems to avoid infinite loop from object reference changes
  const gurusList = dynamicItems?.gurusList || [];
  const countriesList = dynamicItems?.countriesList || [];
  
  // Use stringified versions in dependency array to compare by content, not reference
  // This prevents infinite loops when dynamicItems object reference changes but content doesn't
  const gurusListKey = JSON.stringify(gurusList);
  const countriesListKey = JSON.stringify(countriesList);
  const menuItemsKey = JSON.stringify(menuItems);
  
  // Update menu items when dynamic items or userEmail changes
  useEffect(() => {
    if (menuItems.length > 0) {
      setMenuItemsList(menuItems);
    } else if (userEmail || gurusList.length > 0 || countriesList.length > 0) {
      const items = getMenuItems(userEmail, { gurusList, countriesList });
      setMenuItemsList(items);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemsKey, userEmail, gurusListKey, countriesListKey]);

  // Initialize Fuse.js instance for menu items
  const menuFuse = useMemo(
    () =>
      new Fuse(menuItemsList, {
        keys: ["title", "category", "keywords"],
        threshold: 0.3,
        minMatchCharLength: 1,
      }),
    [menuItemsList]
  );

  // Fetch symbols list only once on component mount
  useEffect(() => {
    const fetchSymbolsList = async () => {
      try {
        const response = await fetch(Constants.INITIALIZE_URL);
        const data = await response.json();
        setSymbolsList(data.symbolsList || []);
      } catch (error) {
        console.error("Error fetching symbols list:", error);
      }
    };
    fetchSymbolsList();
  }, []);

  // Update value when initialValue changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Format stock results for display
  const formatStockResult = useCallback((stock) => ({
    type: "stock",
    title: stock.title,
    description: stock.description || "Stock/ETF",
    image: stock.image,
    price: "📈 Stock",
  }));

  // Format menu item results for display
  const formatMenuResult = useCallback((menuItem) => ({
    type: "menu",
    title: menuItem.title,
    description: menuItem.category ? `📁 ${menuItem.category}` : "Menu Item",
    category: menuItem.category,
    // Pass through navigation metadata
    navigationType: menuItem.navigationType,
    urlTemplate: menuItem.urlTemplate,
    displayState: menuItem.displayState,
    stateUpdate: menuItem.stateUpdate,
    specialHandler: menuItem.specialHandler,
    searchValue: menuItem.searchValue,
    countryCode: menuItem.countryCode,
    // For semantic-ui Search component display
    price: "📁 Menu",
  }));

  // Combined search for stocks and menu items
  const searchSymbols = useCallback(
    (searchValue) => {
      if (searchValue.length < 1) {
        setSymbolResults([]);
        return;
      }

      // Search stocks
      const stockResults = stockFuse
        .search(searchValue, { limit: 10 })
        .map((r) => formatStockResult(r.item));

      // Search menu items
      const menuResults = menuFuse
        .search(searchValue, { limit: 10 })
        .map((r) => formatMenuResult(r.item));

      // Combine and limit total results (prioritize stocks, then menu items)
      // Keep top 8 stocks and top 7 menu items for better UX
      const combinedResults = [
        ...stockResults.slice(0, 8),
        ...menuResults.slice(0, 7),
      ];

      setSymbolResults(combinedResults);
    },
    [stockFuse, menuFuse, formatStockResult, formatMenuResult]
  );

  // Debounce search for better performance
  const debouncedSearch = useMemo(
    () => _.debounce(searchSymbols, 500, { leading: true, trailing: true }),
    [searchSymbols]
  );

  const handleSearchChange = useCallback(
    (e, { value }) => {
      setIsLoading(true);
      setValue(value);
      debouncedSearch(value);
      setIsLoading(false);
    },
    [debouncedSearch]
  );

  const handleResultSelect = useCallback(
    (e, { result }) => {
      setValue(result.title);
      
      if (result.type === "menu" && onMenuSelect) {
        // Call menu navigation handler with menu item data
        onMenuSelect(result);
      } else if (result.type === "stock" && onSymbolSelect) {
        // Call stock selection handler
        onSymbolSelect(result.title);
      }
    },
    [onSymbolSelect, onMenuSelect]
  );

  return (
    <Search
      loading={isLoading}
      onResultSelect={handleResultSelect}
      onSearchChange={handleSearchChange}
      results={symbolResults}
      value={value}
      placeholder={placeholder}
      fluid={fluid}
      style={style}
      className={className}
    />
  );
};

export default React.memo(SymbolSearch);
